export const initNavigation = () => {
    const body = document.querySelector('body');
    const menu = document.querySelector('.main-navigation');

    // Return early if the navigation doesn't exist.
	if ( ! menu ) {
		return;
	}

    const toggleButtons = document.querySelectorAll('.action--toggle-menu');
    const header = document.querySelector('.site-header');
    const submenuToggleButtons = document.querySelectorAll('.menu-item-has-children');


    const toggleMenu = (show) => {
        header.classList.toggle('menu-open', show);
        header.classList.toggle('menu-closed', !show);
        body.classList.toggle('no-scroll', show); // Prevents scrolling while menu open
        // Keep every toggler's aria-expanded in sync with the actual menu
        // state, regardless of what triggered the change (button click,
        // click-outside, etc.), so screen readers announce it correctly.
        toggleButtons.forEach(btn => btn.setAttribute('aria-expanded', show ? 'true' : 'false'));
    };

    // Mega-menu items get their aria-expanded (on the trigger) and
    // aria-hidden (on the panel) toggled in lockstep with the .active
    // class. The CSS-only visibility toggle works without these, but
    // screen readers and keyboard users rely on them being accurate.
    const syncMegaPanelAria = () => {
        document.querySelectorAll('.menu-item--has-mega-panel').forEach(item => {
            const isOpen = item.classList.contains('active');
            const trigger = item.querySelector(':scope > a[aria-controls]');
            const panel = item.querySelector(':scope > .mega-panel');
            if (trigger) trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
            if (panel) panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
        });
    };

    // Flip a mega panel's anchor side when the default (left-anchored)
    // position would push it past the viewport's right edge. Adding
    // .mega-panel--align-right swaps left:-15px for right:-15px in CSS,
    // so the panel grows leftward instead. Measurement works on closed
    // panels (opacity:0 doesn't affect layout); reset-then-test makes
    // it idempotent. Always called BEFORE the .active class is added so
    // the opacity transition starts from the correct anchor — no jump.
    const alignMegaPanel = (item) => {
        const panel = item.querySelector(':scope > .mega-panel');
        if (!panel) return;
        panel.classList.remove('mega-panel--align-right');
        const rect = panel.getBoundingClientRect();
        if (rect.right > window.innerWidth - 16) {
            panel.classList.add('mega-panel--align-right');
        }
    };

    // Function to close all submenus
    const closeAllSubmenus = () => {
        submenuToggleButtons.forEach(btn => {
            btn.classList.remove('active');
            delete btn.dataset.hoverOpened;
        });
        syncMegaPanelAria();
    };

    // Toggle menu
    toggleButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            toggleMenu(!header.classList.contains('menu-open'));
        });
    });

    // Toggle submenus
    submenuToggleButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
            // Only prevent default and toggle if clicking the parent menu item itself
            // and not a child link
            if (e.target === btn || e.target === btn.querySelector('a')) {
                e.preventDefault();
                e.stopPropagation(); // Prevent click from bubbling to document

                // First-click immunity for hover-opened mega panels.
                // Hover opens the panel; users instinctively click the
                // trigger to "confirm" ("does this site need a click?").
                // Without this guard the click would toggle the panel
                // closed mid-intent. Swallow the click and clear the flag
                // so subsequent clicks toggle normally.
                if (btn.dataset.hoverOpened === 'true') {
                    delete btn.dataset.hoverOpened;
                    return;
                }

                // Close all other submenus (and clear any stale immunity
                // flag on them, so they don't carry it across opens).
                submenuToggleButtons.forEach(otherBtn => {
                    if (otherBtn !== btn) {
                        otherBtn.classList.remove('active');
                        delete otherBtn.dataset.hoverOpened;
                    }
                });

                // Pre-position the panel before opening so the transition
                // starts from the correct anchor side.
                if (!btn.classList.contains('active') && btn.classList.contains('menu-item--has-mega-panel')) {
                    alignMegaPanel(btn);
                }

                // Toggle the clicked submenu
                btn.classList.toggle('active');
                syncMegaPanelAria();
            }
        });
    });

    // Hover + focus opening for mega-menu panels.
    //
    // Click toggling above covers touch and explicit user intent;
    // hover/focus are progressive enhancements for mouse and keyboard
    // users. All paths share the same .active class on the parent <li>,
    // so CSS visibility, click-outside, and Escape continue to work
    // uniformly.
    //
    // The 240ms close delay forgives the brief "no element under cursor"
    // moment when the mouse traverses from trigger to panel — and the
    // .mega-panel::before bridge in CSS extends the panel's hit area up
    // into that gap so the close timer often gets cancelled before it
    // fires anyway. The close handler also bails when focus is still
    // inside the item, so a click-to-open followed by mouse-away keeps
    // the panel pinned for the keyboard user who originated the click.
    const megaPanelItems = document.querySelectorAll('.menu-item--has-mega-panel');
    const HOVER_OPEN_DELAY = 80;
    // Generous close delay so the open transition (200ms opacity + translate)
    // has time to settle before the close timer fires. The .mega-panel::before
    // bridge isn't at its final position during the transform animation, so
    // a cursor sitting in the visual gap won't be on the bridge for the first
    // ~200ms — needs to be > transition duration with a bit of margin.
    const HOVER_CLOSE_DELAY = 250;

    // Gate the entire hover/focus enhancement on the device actually having
    // a hover-capable pointer (i.e. a mouse). Touch devices don't match, so
    // the click handler above is the only path that runs — which means the
    // first-click immunity flag never gets set, and a tap toggles the panel
    // on/off normally. No UA sniffing involved; the browser tells us.
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    if (canHover) megaPanelItems.forEach(item => {
        const panel = item.querySelector(':scope > .mega-panel');
        if (!panel) return;

        let openTimer = null;
        let closeTimer = null;

        const open = () => {
            clearTimeout(openTimer);
            clearTimeout(closeTimer);
            // Always re-arm immunity on every hover-driven open, including
            // re-opens after the panel was just closed. Users instinctively
            // click-confirm each fresh open; without re-arming, a close →
            // hover-reopen → click sequence would close the panel mid-intent.
            // To close via mouse, the user mouseleaves entirely (close
            // timer fires) or clicks outside / hits Escape.
            megaPanelItems.forEach(other => {
                if (other !== item) {
                    other.classList.remove('active');
                    delete other.dataset.hoverOpened;
                }
            });
            alignMegaPanel(item);
            item.classList.add('active');
            item.dataset.hoverOpened = 'true';
            syncMegaPanelAria();
        };

        const closeIfBlurred = () => {
            if (item.contains(document.activeElement)) return;
            item.classList.remove('active');
            delete item.dataset.hoverOpened;
            syncMegaPanelAria();
        };

        const scheduleOpen = () => {
            clearTimeout(closeTimer);
            openTimer = setTimeout(open, HOVER_OPEN_DELAY);
        };

        const scheduleClose = () => {
            clearTimeout(openTimer);
            closeTimer = setTimeout(closeIfBlurred, HOVER_CLOSE_DELAY);
        };

        item.addEventListener('mouseenter', scheduleOpen);
        item.addEventListener('mouseleave', scheduleClose);
        panel.addEventListener('mouseenter', () => clearTimeout(closeTimer));
        panel.addEventListener('mouseleave', scheduleClose);

        item.addEventListener('focusin', open);
        item.addEventListener('focusout', (e) => {
            if (!item.contains(e.relatedTarget)) {
                scheduleClose();
            }
        });
    });

    // Escape closes any open submenu / mega panel.
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            closeAllSubmenus();
        }
    });

    // Close when clicking outside
    document.addEventListener('click', (e) => {
        // Mobile menu
        if (!menu.contains(e.target) && header.classList.contains('menu-open')) {
            toggleMenu(!header.classList.contains('menu-open'));
        }

        // Dropdown menu
        const isClickInsideMenu = Array.from(submenuToggleButtons).some(btn => 
            btn.contains(e.target)
        );
        
        if (!isClickInsideMenu) {
            closeAllSubmenus();
        }
    });
};